import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Luggage,
  Plane,
  Briefcase,
  GlassWater,
  Route,
  SlidersHorizontal,
  Clock,
  EyeOff,
  Armchair,
  UserCheck,
} from "lucide-react";
import SEO from "@/components/SEO";
import { ShieldWm } from "@/components/Watermark";
import { FadeUp, MaskedLine, HeroLine, HeroFade, Eyebrow, DrawLine, Stagger, StaggerItem, ParallaxImg } from "@/components/Reveal";
import QuoteCTA from "@/components/QuoteCTA";
import ItalyMap from "@/components/ItalyMap";

const HERO_IMG =
  "https://images.unsplash.com/photo-1485291571150-772bcfc10da5?q=80&w=2000&auto=format&fit=crop";
const KHALID_VAN =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_elite-transfer-italy/artifacts/l9q0pedw_IMG-20260521-WA0042%281%29.jpg";
const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1605437241278-c1806d14a4d9?q=80&w=2000&auto=format&fit=crop";

const SERVICES = [
  {
    n: "01",
    icon: Luggage,
    t: "Trasferimenti privati",
    d: "Spostamenti personalizzati per viaggi di lavoro, occasioni private e trasferimenti individuali.",
  },
  {
    n: "02",
    icon: Plane,
    t: "Trasferimenti aeroportuali",
    d: "Servizio da e verso i principali aeroporti italiani, con particolare attenzione alla puntualità.",
  },
  {
    n: "03",
    icon: Briefcase,
    t: "Servizi business",
    d: "Soluzioni dedicate a professionisti, manager e aziende che necessitano di un servizio discreto e affidabile.",
  },
  {
    n: "04",
    icon: GlassWater,
    t: "Eventi e cerimonie",
    d: "Servizio chauffeur per matrimoni, eventi, ricevimenti e occasioni speciali.",
  },
  {
    n: "05",
    icon: Route,
    t: "Trasferimenti a lunga distanza",
    d: "Viaggi personalizzati tra città italiane, anche su lunghe distanze.",
  },
  {
    n: "06",
    icon: SlidersHorizontal,
    t: "Servizi personalizzati",
    d: "Possibilità di organizzare servizi su misura in base alle necessità del cliente.",
  },
];

const WHY = [
  {
    icon: Clock,
    t: "Puntualità",
    d: "Ogni trasferimento viene organizzato con attenzione ai tempi e agli appuntamenti del cliente.",
  },
  {
    icon: EyeOff,
    t: "Discrezione",
    d: "Professionalità e riservatezza durante ogni viaggio.",
  },
  {
    icon: Armchair,
    t: "Comfort",
    d: "Un'esperienza di viaggio confortevole e rilassata.",
  },
  {
    icon: UserCheck,
    t: "Servizio personalizzato",
    d: "Ogni richiesta viene valutata direttamente con il cliente.",
  },
];

const VALUES = ["Professionalità", "Puntualità", "Discrezione", "Comfort"];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[86svh] flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          fetchPriority="high"
          alt="Berlina executive nera di sera — Zaichauffer autista privato e chauffeur"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-ink/55" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to top, #080808 6%, rgba(8,8,8,0.15) 48%, rgba(8,8,8,0.45) 100%)",
        }}
      />

      <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-40 md:pb-20">
        <HeroFade delay={0.1}>
          <Eyebrow>Private Chauffeur Service — Italia</Eyebrow>
        </HeroFade>
        <h1
          data-testid="hero-title"
          className="mt-8 font-serif text-[13vw] leading-[1.02] tracking-tight text-bone sm:text-7xl lg:text-8xl"
        >
          <HeroLine delay={0.2}>IL TUO VIAGGIO.</HeroLine>
          <HeroLine delay={0.35}>LA NOSTRA PRIORITÀ.</HeroLine>
        </h1>
        <HeroFade delay={0.55}>
          <p className="mt-8 max-w-xl text-[15px] font-light leading-relaxed text-bone/80 md:text-base">
            Servizio di chauffeur privato per trasferimenti in tutta Italia.
          </p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link
              data-testid="hero-cta-preventivo"
              to="/richiedi-preventivo"
              className="btn-solid group"
            >
              Richiedi un preventivo
              <ArrowRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
            <Link
              data-testid="hero-cta-contatti"
              to="/contatti"
              className="btn-outline"
            >
              Contattaci
            </Link>
          </div>
        </HeroFade>
      </div>

      <HeroFade delay={0.8}>
        <div className="relative border-t border-white/10 bg-ink/40 backdrop-blur-sm">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-2 px-6 py-5">
            {VALUES.map((v, i) => (
              <span
                key={v}
                className="flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase text-bone/70"
              >
                {i > 0 && <span className="h-1 w-1 rounded-full bg-gold/80" />}
                {v}
              </span>
            ))}
          </div>
        </div>
      </HeroFade>
    </section>
  );
}

function Intro() {
  return (
    <section className="relative isolate mx-auto max-w-7xl overflow-hidden px-6 py-24 md:py-36" data-testid="intro-section">
      <ShieldWm className="-z-10 -right-36 top-1/2 w-[480px] -translate-y-1/2 opacity-[0.05]" />
      <div className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <FadeUp>
            <Eyebrow>Il servizio</Eyebrow>
            <h2 className="mt-8 font-serif text-3xl leading-tight text-bone sm:text-4xl lg:text-5xl">
              Un servizio pensato per chi cerca qualcosa in più.
            </h2>
            <DrawLine className="mt-8" />
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mt-10 max-w-xl text-[15px] font-light leading-relaxed text-mute">
              Zaichauffer offre un servizio di autista privato dedicato a chi
              desidera viaggiare con comfort, puntualità e discrezione. Ogni
              trasferimento viene organizzato con attenzione alle esigenze del
              cliente, dal primo contatto fino all'arrivo a destinazione.
            </p>
            <div className="mt-10 border-l-2 border-gold pl-6">
              <p className="font-serif text-xl italic leading-snug text-bone/90 md:text-2xl">
                Un servizio personale, senza intermediari: sarai sempre in
                contatto diretto con Khalid El Aissaoui.
              </p>
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-6">
          <FadeUp delay={0.2} className="lg:pl-12 lg:pt-12">
            <div className="frame-gold">
              <ParallaxImg
                src={KHALID_VAN}
                alt="Khalid El Aissaoui, titolare e chauffeur di Zaichauffer, al volante del veicolo"
                className="relative z-10 aspect-[4/5] w-full"
              />
            </div>
            <p className="label-cap mt-6 !text-[9px]">
              Khalid El Aissaoui — Titolare e Chauffeur
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section className="relative isolate overflow-hidden border-y border-gold/10 bg-coal py-24 md:py-36" data-testid="services-section">
      <ShieldWm className="-z-10 -left-28 -top-28 w-[420px] -rotate-12 opacity-[0.04]" />
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <FadeUp>
            <Eyebrow>Cosa facciamo</Eyebrow>
            <h2 className="mt-8 font-serif text-3xl text-bone sm:text-4xl lg:text-5xl">
              I nostri servizi
            </h2>
            <DrawLine className="mt-8" />
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link to="/richiedi-preventivo" className="lux-link group">
              Richiedi un preventivo
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>

        <div className="mt-16 grid gap-px border border-white/5 bg-white/5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <FadeUp
              key={s.n}
              delay={0.06 * i}
              className="bg-coal"
              data-testid={`service-card-${i + 1}`}
            >
              <article
                data-testid={`service-card-${i + 1}`}
                className="card-lux h-full border-0 p-10 lg:p-12"
              >
                <div className="flex items-center justify-between">
                  <span className="font-serif text-sm italic text-gold">
                    {s.n}
                  </span>
                  <s.icon size={22} strokeWidth={1.25} className="text-gold/80" />
                </div>
                <h3 className="mt-14 font-serif text-2xl leading-snug text-bone">
                  {s.t.toUpperCase()}
                </h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-mute">
                  {s.d}
                </p>
              </article>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function DoveOperiamo() {
  return (
    <section className="relative isolate overflow-hidden border-y border-gold/10 bg-coal py-24 md:py-36" data-testid="coverage-section">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_72%_50%,rgba(198,169,107,0.08),transparent_55%)]" />
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-16 lg:grid-cols-2">
        <div>
          <FadeUp>
            <Eyebrow>Dove operiamo</Eyebrow>
            <h2 className="mt-8 font-serif text-3xl leading-tight text-bone sm:text-4xl lg:text-5xl">
              Ovunque devi andare.
            </h2>
            <DrawLine className="mt-8" />
            <p className="mt-8 max-w-lg text-[15px] font-light leading-relaxed text-mute">
              Operiamo in tutta Italia e, su richiesta, anche in Europa.
              Aeroporti, città
              d'affari, eventi o la semplice voglia di viaggiare: indichi la
              destinazione e pensiamo noi a tutto il resto, con la puntualità e
              la discrezione che ci contraddistinguono. Ogni viaggio è seguito
              personalmente da Khalid.
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.2} className="lg:pl-16">
          <div className="relative frame-gold">
            <ItalyMap className="relative z-10 w-full" />
            <span className="absolute left-[calc(36%-6px)] top-[calc(26.4%-6px)] z-20 flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-50" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-gold" />
            </span>
          </div>
        </FadeUp>
      </div>
      </div>
    </section>
  );
}

function Differentiator() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="differentiator-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-[1.18]">
        <img
          src={INTERIOR_IMG}
          alt="Interni eleganti di un veicolo executive — servizio chauffeur di lusso"
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-ink/85" />
      <span
        aria-hidden="true"
        className="text-outline-gold pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none whitespace-nowrap font-serif text-[16vw] tracking-[0.08em]"
      >
        ZAICHAUFFER
      </span>

      <div className="relative mx-auto max-w-7xl px-6 py-28 md:py-44">
        <FadeUp>
          <Eyebrow>La differenza</Eyebrow>
        </FadeUp>
        <h2 className="mt-10 font-serif leading-[1.08] text-bone">
          <MaskedLine>
            <span className="text-4xl tracking-tight sm:text-5xl lg:text-7xl">
              NON È SOLO UN TRASFERIMENTO.
            </span>
          </MaskedLine>
          <MaskedLine delay={0.2}>
            <span className="text-4xl italic tracking-tight text-gold sm:text-5xl lg:text-7xl">
              È IL TUO TEMPO.
            </span>
          </MaskedLine>
        </h2>
        <FadeUp delay={0.3}>
          <p className="mt-12 max-w-2xl text-[15px] font-light leading-relaxed text-bone/75 md:text-base">
            Che tu stia andando a un incontro di lavoro, prendendo un volo o
            semplicemente raggiungendo la tua destinazione, ogni viaggio viene
            organizzato per permetterti di concentrarti su ciò che conta.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section className="relative isolate mx-auto max-w-7xl overflow-hidden px-6 py-24 md:py-36" data-testid="why-section">
      <FadeUp>
        <Eyebrow>Perché Zaichauffer</Eyebrow>
      </FadeUp>
      <Stagger className="mt-16 grid gap-12 sm:grid-cols-2 lg:grid-cols-4" gap={0.1}>
        {WHY.map((w) => (
          <StaggerItem key={w.t}>
            <div className="hairline mb-8 w-12" />
            <w.icon size={26} strokeWidth={1.1} className="text-gold" />
            <h3 className="mt-6 font-serif text-xl tracking-wide text-bone">
              {w.t.toUpperCase()}
            </h3>
            <p className="mt-4 text-sm font-light leading-relaxed text-mute">
              {w.d}
            </p>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="Zaichauffer | Autista Privato e Chauffeur in Italia"
        description="Zaichauffer offre servizi di autista privato e chauffeur per trasferimenti in tutta Italia e in Europa. Professionalità, puntualità, comfort e discrezione."
      />
      <Hero />
      <Intro />
      <Services />
      <DoveOperiamo />
      <Differentiator />
      <Why />
      <QuoteCTA />
    </>
  );
}
